import { NalType, nalType } from '../h264/h264.ts';
import type { AccessUnit } from '../h264/h264_depacketizer.ts';

// Fragmented MP4 (ISO/IEC 14496-12) as HLS gives it (docs/specs/video-hls.md VH-3): the H.264
// track of an initialisation section, and the samples of that track in each fragment (`moof`
// and `mdat`), turned into the access units the decoder takes. Other tracks (audio) are left out.

/** What the initialisation section says of the video track. */
export interface VideoTrack {
    id: number;
    /** Units per second of its times. */
    timescale: number;
    /** Bytes before each NAL unit in a sample (`avcC`'s lengthSizeMinusOne + 1). */
    lengthSize: number;
    sps: Uint8Array | undefined;
    pps: Uint8Array | undefined;
    /** `trex`: what a fragment leaves out. */
    defaults: { duration: number; size: number; flags: number };
}

/** One picture of the video track, as a fragment holds it. */
export interface VideoSample {
    /** In the track's timescale. */
    decodeTime: number;
    /** Presentation time minus decode time. */
    compositionOffset: number;
    duration: number;
    key: boolean;
    /** NAL units, each after its length. */
    data: Uint8Array;
}

interface Box {
    type: string;
    /** Where the box starts in the bytes it was found in. */
    start: number;
    /** Its content, after the header. */
    body: Uint8Array;
}

const view = (bytes: Uint8Array) : DataView => new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
const typeAt = (bytes: Uint8Array, at: number) : string => String.fromCharCode(bytes[at]!, bytes[at + 1]!, bytes[at + 2]!, bytes[at + 3]!);

/** The boxes one after the other in `bytes`. */
export function boxesOf(bytes: Uint8Array) : Box[] {
    const boxes: Box[] = [];
    const data = view(bytes);
    let at = 0;
    while (at + 8 <= bytes.length) {
        let size = data.getUint32(at);
        let header = 8;
        if (size === 1 && at + 16 <= bytes.length) {
            size = Number(data.getBigUint64(at + 8));
            header = 16;
        } else if (size === 0) {
            size = bytes.length - at;
        }
        if (size < header || at + size > bytes.length) {
            break;
        }
        boxes.push({ type: typeAt(bytes, at + 4), start: at, body: bytes.subarray(at + header, at + size) });
        at += size;
    }

    return boxes;
}

const child = (box: Box | undefined, type: string) : Box | undefined => (box ? boxesOf(box.body).find((each) => each.type === type) : undefined);
const children = (box: Box, type: string) : Box[] => boxesOf(box.body).filter((each) => each.type === type);

/** `avcC`: the NAL length size and the first SPS and PPS. */
function avcConfiguration(body: Uint8Array) : { lengthSize: number; sps: Uint8Array | undefined; pps: Uint8Array | undefined } {
    const data = view(body);
    const lengthSize = (body[4]! & 0x03) + 1;
    let at = 5;
    const read = (count: number) : Uint8Array[] => {
        const sets: Uint8Array[] = [];
        for (let index = 0; index < count && at + 2 <= body.length; index++) {
            const length = data.getUint16(at);
            sets.push(body.subarray(at + 2, at + 2 + length));
            at += 2 + length;
        }
        return sets;
    };
    const [sps] = read(body[at++]! & 0x1f);
    const [pps] = read(body[at++] ?? 0);

    return { lengthSize, sps, pps };
}

/** The first H.264 video track of an initialisation section (`ftyp`, `moov`); undefined without one. */
export function parseInit(bytes: Uint8Array) : VideoTrack | undefined {
    const moov = boxesOf(bytes).find((box) => box.type === 'moov');
    if (!moov) {
        return undefined;
    }
    const extends_ = child(moov, 'mvex');
    for (const trak of children(moov, 'trak')) {
        const mdia = child(trak, 'mdia');
        const handler = child(mdia, 'hdlr');
        if (!handler || typeAt(handler.body, 8) !== 'vide') {
            continue;
        }
        const tkhd = child(trak, 'tkhd')!;
        const id = view(tkhd.body).getUint32(tkhd.body[0] === 1 ? 20 : 12);
        const mdhd = child(mdia, 'mdhd')!;
        const timescale = view(mdhd.body).getUint32(mdhd.body[0] === 1 ? 20 : 12);
        const stsd = child(child(child(mdia, 'minf'), 'stbl'), 'stsd');
        // stsd: version and flags, entry count; then the sample entries.
        const entry = stsd ? boxesOf(stsd.body.subarray(8)).find((box) => box.type === 'avc1' || box.type === 'avc3') : undefined;
        if (!entry) {
            continue;
        }
        // A visual sample entry has 78 bytes of fields before its own boxes.
        const avcC = boxesOf(entry.body.subarray(78)).find((box) => box.type === 'avcC');
        const configuration = avcC ? avcConfiguration(avcC.body) : { lengthSize: 4, sps: undefined, pps: undefined };
        const trex = extends_ ? children(extends_, 'trex').find((box) => view(box.body).getUint32(4) === id) : undefined;
        const defaults = trex ? { duration: view(trex.body).getUint32(12), size: view(trex.body).getUint32(16), flags: view(trex.body).getUint32(20) } : { duration: 0, size: 0, flags: 0 };

        return { id, timescale, ...configuration, defaults };
    }

    return undefined;
}

const NON_SYNC = 0x0001_0000;

/** The video track's samples in fragments (`moof` and `mdat`, as many as there are), in order. */
export function parseFragments(bytes: Uint8Array, track: VideoTrack) : VideoSample[] {
    const samples: VideoSample[] = [];
    for (const moof of boxesOf(bytes).filter((box) => box.type === 'moof')) {
        for (const traf of children(moof, 'traf')) {
            const tfhd = child(traf, 'tfhd');
            if (!tfhd || view(tfhd.body).getUint32(4) !== track.id) {
                continue;
            }
            const header = view(tfhd.body);
            const headerFlags = header.getUint32(0) & 0xff_ffff;
            let at = 8;
            let base = moof.start;
            if (headerFlags & 0x01) {
                base = Number(header.getBigUint64(at));
                at += 8;
            }
            if (headerFlags & 0x02) {
                at += 4;
            }
            const duration = headerFlags & 0x08 ? header.getUint32((at += 4) - 4) : track.defaults.duration;
            const size = headerFlags & 0x10 ? header.getUint32((at += 4) - 4) : track.defaults.size;
            const flags = headerFlags & 0x20 ? header.getUint32((at += 4) - 4) : track.defaults.flags;

            const tfdt = child(traf, 'tfdt');
            let time = tfdt ? (tfdt.body[0] === 1 ? Number(view(tfdt.body).getBigUint64(4)) : view(tfdt.body).getUint32(4)) : 0;

            for (const trun of children(traf, 'trun')) {
                const run = view(trun.body);
                const version = trun.body[0]!;
                const runFlags = run.getUint32(0) & 0xff_ffff;
                const count = run.getUint32(4);
                let field = 8;
                let offset = base;
                if (runFlags & 0x01) {
                    offset = base + run.getInt32(field);
                    field += 4;
                }
                let firstFlags: number | undefined;
                if (runFlags & 0x04) {
                    firstFlags = run.getUint32(field);
                    field += 4;
                }
                for (let index = 0; index < count; index++) {
                    const sampleDuration = runFlags & 0x100 ? run.getUint32((field += 4) - 4) : duration;
                    const sampleSize = runFlags & 0x200 ? run.getUint32((field += 4) - 4) : size;
                    const sampleFlags = runFlags & 0x400 ? run.getUint32((field += 4) - 4) : index === 0 && firstFlags !== undefined ? firstFlags : flags;
                    const composition = runFlags & 0x800 ? (version === 0 ? run.getUint32((field += 4) - 4) : run.getInt32((field += 4) - 4)) : 0;
                    if (offset + sampleSize <= bytes.length) {
                        samples.push({ decodeTime: time, compositionOffset: composition, duration: sampleDuration, key: (sampleFlags & NON_SYNC) === 0, data: bytes.subarray(offset, offset + sampleSize) });
                    }
                    offset += sampleSize;
                    time += sampleDuration;
                }
            }
        }
    }

    return samples;
}

/**
 * A sample as the decoder takes it: its NAL units without their lengths, the parameter sets and
 * delimiters taken out (as the RTP depacketiser does), and the parameter sets in force on a key
 * picture. `parameters` carries them from sample to sample: in-band ones replace the `avcC`'s.
 */
export function accessUnitOf(sample: VideoSample, track: VideoTrack, parameters: { sps: Uint8Array | undefined; pps: Uint8Array | undefined }) : AccessUnit {
    const nalus: Uint8Array[] = [];
    const data = view(sample.data);
    let idr = false;
    for (let at = 0; at + track.lengthSize <= sample.data.length;) {
        const length = track.lengthSize === 4 ? data.getUint32(at) : track.lengthSize === 2 ? data.getUint16(at) : track.lengthSize === 1 ? data.getUint8(at) : (data.getUint16(at) << 8) | data.getUint8(at + 2);
        const nal = sample.data.subarray(at + track.lengthSize, at + track.lengthSize + length);
        at += track.lengthSize + length;
        const type = nalType(nal);
        if (type === NalType.Sps) {
            parameters.sps = nal;
        } else if (type === NalType.Pps) {
            parameters.pps = nal;
        } else if (type !== NalType.AccessUnitDelimiter && nal.length > 0) {
            idr ||= type === NalType.Idr;
            nalus.push(nal);
        }
    }
    const key = sample.key || idr;
    // The presentation time, so that pictures that come out of order show in order.
    const timestamp = sample.decodeTime + sample.compositionOffset;

    return key && parameters.sps && parameters.pps ? { timestamp, key, nalus, sps: parameters.sps, pps: parameters.pps } : { timestamp, key, nalus };
}
